import { getAllCountryCurrencies } from "country-info-flags";
import countryCurrencyData from "react-country-flag-currency-picker/lib/countries-currency.js";

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  countryName: string;
  countryCode: string;
}

class CurrencyService {
  private static instance: CurrencyService;
  private currencies: Currency[] = [];
  private isLoading = false;
  private loadPromise: Promise<Currency[]> | null = null;

  // Priority countries for common currencies
  private priorityCountries: Record<string, { countryCode: string, countryName: string }> = {
    'USD': { countryCode: 'US', countryName: 'United States' },
    'EUR': { countryCode: 'EU', countryName: 'European Union' },
    'GBP': { countryCode: 'GB', countryName: 'United Kingdom' },
    'JPY': { countryCode: 'JP', countryName: 'Japan' },
    'CAD': { countryCode: 'CA', countryName: 'Canada' },
    'AUD': { countryCode: 'AU', countryName: 'Australia' },
    'CHF': { countryCode: 'CH', countryName: 'Switzerland' },
    'CNY': { countryCode: 'CN', countryName: 'China' },
    'INR': { countryCode: 'IN', countryName: 'India' },
    'BRL': { countryCode: 'BR', countryName: 'Brazil' },
  };

  private constructor() {}

  public static getInstance(): CurrencyService {
    if (!CurrencyService.instance) {
      CurrencyService.instance = new CurrencyService();
    }
    return CurrencyService.instance;
  }

  private async loadCurrencies(): Promise<Currency[]> {
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = new Promise(async (resolve, reject) => {
      try {
        const countries = getAllCountryCurrencies();
        const currencyMap = new Map<string, Currency>();

        // First pass: add priority currencies
        Object.entries(this.priorityCountries).forEach(([currencyCode, priority]) => {
          if (currencyCode === 'EUR') {
            // Special handling for Euro (European Union)
            const currency: Currency = {
              code: currencyCode,
              name: this.getCurrencyName(currencyCode),
              symbol: this.getCurrencySymbol('EU', currencyCode),
              countryName: priority.countryName,
              countryCode: priority.countryCode,
            };
            currencyMap.set(currencyCode, currency);
          } else {
            // Find the country data for other currencies
            const country = countries.find((c: any) => c.code === priority.countryCode && c.currency === currencyCode);
            if (country) {
              const currency: Currency = {
                code: currencyCode,
                name: this.getCurrencyName(currencyCode),
                symbol: this.getCurrencySymbol(priority.countryCode, currencyCode),
                countryName: priority.countryName,
                countryCode: priority.countryCode,
              };
              currencyMap.set(currencyCode, currency);
            }
          }
        });

        // Second pass: process all other countries
        countries.forEach((country: any) => {
          const { code: countryCode, name: countryName, currency: currencyCode } = country;
          
          if (!currencyCode) return;

          // Skip if we already have this currency (from priority countries)
          if (currencyMap.has(currencyCode)) return;

          // Create currency object
          const currency: Currency = {
            code: currencyCode,
            name: this.getCurrencyName(currencyCode),
            symbol: this.getCurrencySymbol(countryCode, currencyCode),
            countryName,
            countryCode,
          };
          
          currencyMap.set(currencyCode, currency);
        });

        this.currencies = Array.from(currencyMap.values());
        this.sortCurrencies();
        resolve(this.currencies);
      } catch (error) {
        console.error('Error loading currencies:', error);
        reject(error);
      } finally {
        this.isLoading = false;
      }
    });

    return this.loadPromise;
  }

  private getCurrencyName(code: string): string {
    const nameMap: Record<string, string> = {
      'USD': 'US Dollar',
      'EUR': 'Euro',
      'GBP': 'British Pound',
      'JPY': 'Japanese Yen',
      'CAD': 'Canadian Dollar',
      'AUD': 'Australian Dollar',
      'CHF': 'Swiss Franc',
      'CNY': 'Chinese Yuan',
      'INR': 'Indian Rupee',
      'BRL': 'Brazilian Real',
    };

    return nameMap[code] || `${code} Currency`;
  }

  private getCurrencySymbol(countryCode: string, currencyCode: string): string {
    // Get symbol from react-country-flag-currency-picker data
    const countryData = countryCurrencyData[countryCode];
    if (countryData && countryData.symbol) {
      return countryData.symbol;
    }
    
    // Fallback for common currencies
    const symbolMap: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥',
      'INR': '₹',
      'BRL': 'R$',
    };

    return symbolMap[currencyCode] || currencyCode;
  }

  private sortCurrencies(): void {
    const commonCurrencyCodes = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'BRL'];
    
    this.currencies.sort((a, b) => {
      const aIndex = commonCurrencyCodes.indexOf(a.code);
      const bIndex = commonCurrencyCodes.indexOf(b.code);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      return a.code.localeCompare(b.code);
    });
  }

  public async getAllCurrencies(): Promise<Currency[]> {
    if (this.currencies.length > 0) {
      return this.currencies;
    }
    return await this.loadCurrencies();
  }

  public async getCurrencyByCode(code: string): Promise<Currency | undefined> {
    const currencies = await this.getAllCurrencies();
    return currencies.find(currency => currency.code === code);
  }

  public async searchCurrencies(query: string): Promise<Currency[]> {
    const currencies = await this.getAllCurrencies();
    const lowerQuery = query.toLowerCase();
    
    return currencies.filter(currency => 
      currency.code.toLowerCase().includes(lowerQuery) ||
      currency.name.toLowerCase().includes(lowerQuery) ||
      currency.countryName.toLowerCase().includes(lowerQuery) ||
      currency.symbol.toLowerCase().includes(lowerQuery)
    );
  }

  public formatCurrencyDisplay(currency: Currency): string {
    return `${currency.symbol} - ${currency.countryName} ${currency.name}(${currency.symbol})`;
  }

  public formatCurrencyForSelect(currency: Currency): string {
    return `${currency.symbol} - ${currency.countryName} ${currency.name}(${currency.symbol})`;
  }
}

const currencyService = CurrencyService.getInstance();

export { currencyService };
// export type { Currency };
